import {
	Bookmark,
	Earth,
	Forward,
	Heart,
	MessageCircle,
	MoreHorizontal,
	Send,
	ThumbsUp,
	X,
} from "lucide-react";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import type { SocialPreview } from "../../types/flows";

type Mode = "dark" | "light";

const FACEBOOK: Record<Mode, Record<string, string>> = {
	dark: {
		card: "#242526",
		text: "#E4E6EB",
		muted: "#B0B3B8",
		box: "#3A3B3C",
		line: "#3E4042",
		link: "#4599FF",
	},
	light: {
		card: "#FFFFFF",
		text: "#050505",
		muted: "#65676B",
		box: "#F0F2F5",
		line: "#CED0D4",
		link: "#0064D1",
	},
};

const INSTAGRAM: Record<Mode, Record<string, string>> = {
	dark: {
		card: "#000000",
		text: "#F5F5F5",
		muted: "#A8A8A8",
		line: "#262626",
		link: "#E0F1FF",
	},
	light: {
		card: "#FFFFFF",
		text: "#000000",
		muted: "#737373",
		line: "#DBDBDB",
		link: "#00376B",
	},
};

const STORAGE_KEY = "flows.socialPreviewMode";

const storedMode = (): Mode => {
	try {
		return localStorage.getItem(STORAGE_KEY) === "light" ? "light" : "dark";
	} catch {
		return "dark";
	}
};

const Avatar = ({
	name,
	picture,
	size,
}: {
	name: string;
	picture: string | null;
	size: number;
}) =>
	picture ? (
		<img
			src={picture}
			alt=""
			width={size}
			height={size}
			className="shrink-0 rounded-full object-cover"
			style={{ width: size, height: size }}
		/>
	) : (
		<span
			className="flex shrink-0 items-center justify-center rounded-full bg-[#1B74E4] font-bold text-white"
			style={{ width: size, height: size, fontSize: size * 0.38 }}
		>
			{name
				.split(/\s+/)
				.map((word) => word[0])
				.join("")
				.slice(0, 2)
				.toUpperCase()}
		</span>
	);

const PostImage = ({
	src,
	aspectRatio,
	background,
	color,
}: {
	src: string;
	aspectRatio: string;
	background: string;
	color: string;
}) => {
	const [failed, setFailed] = useState(false);
	if (failed) {
		return (
			<div
				className="flex flex-col items-center justify-center gap-1 px-6 text-center text-[12px]"
				style={{ aspectRatio, background, color }}
			>
				<span className="font-semibold">The image did not load</span>
				<span className="break-all">{src}</span>
				<span>
					Meta fetches it from this address, so the post would go out without
					it.
				</span>
			</div>
		);
	}
	return (
		<img
			src={src}
			alt=""
			onError={() => setFailed(true)}
			className="block w-full object-cover"
			style={{ aspectRatio, background }}
		/>
	);
};

const withHashtags = (text: string, color: string): ReactNode[] =>
	text.split(/(#[\p{L}\p{N}_]+)/u).map((part, index) =>
		part.startsWith("#") ? (
			<span key={`${index}-${part}`} style={{ color }}>
				{part}
			</span>
		) : (
			part
		),
	);

const Clamped = ({
	text,
	lines,
	more,
	color,
	prefix,
}: {
	text: string;
	lines: number;
	more: string;
	color: { text: string; muted: string; link: string };
	prefix?: ReactNode;
}) => {
	const ref = useRef<HTMLParagraphElement>(null);
	const [open, setOpen] = useState(false);
	const [overflows, setOverflows] = useState(false);

	useLayoutEffect(() => {
		const el = ref.current;
		if (el && !open) setOverflows(el.scrollHeight > el.clientHeight + 1);
	}, [open]);

	return (
		<div>
			<p
				ref={ref}
				className="whitespace-pre-line break-words"
				style={{
					color: color.text,
					display: open ? "block" : "-webkit-box",
					WebkitBoxOrient: "vertical",
					WebkitLineClamp: open ? "unset" : lines,
					overflow: open ? "visible" : "hidden",
				}}
			>
				{prefix}
				{withHashtags(text, color.link)}
			</p>
			{overflows && !open && (
				<button
					type="button"
					onClick={() => setOpen(true)}
					className="font-semibold hover:underline"
					style={{ color: color.muted }}
				>
					{more}
				</button>
			)}
		</div>
	);
};

const FacebookPost = ({ post, mode }: { post: SocialPreview; mode: Mode }) => {
	const c = FACEBOOK[mode];
	const media = post.link?.image ?? post.image;
	return (
		<div
			className="w-full max-w-[500px] overflow-hidden rounded-lg shadow-sm"
			style={{
				background: c.card,
				fontFamily:
					'"Segoe UI Historic", "Segoe UI", Helvetica, Arial, sans-serif',
			}}
		>
			<div className="flex items-start gap-2 px-4 pt-3">
				<Avatar
					name={post.account.name}
					picture={post.account.picture}
					size={40}
				/>
				<div className="min-w-0 flex-1 leading-tight">
					<p className="text-[15px] font-semibold" style={{ color: c.text }}>
						{post.account.name}
					</p>
					<p
						className="mt-0.5 flex items-center gap-1 text-[13px]"
						style={{ color: c.muted }}
					>
						Lige nu · <Earth className="h-3 w-3" />
					</p>
				</div>
				<MoreHorizontal className="h-5 w-5" style={{ color: c.muted }} />
				<X className="h-5 w-5" style={{ color: c.muted }} />
			</div>

			<div className="px-4 pt-2 pb-3 text-[15px] leading-[1.34]">
				<Clamped
					text={post.message}
					lines={3}
					more="Se mere"
					color={{ text: c.text, muted: c.text, link: c.link }}
				/>
			</div>

			{media ? (
				<PostImage
					src={media}
					aspectRatio={post.link ? "1.91 / 1" : "1200 / 630"}
					background={c.box}
					color={c.muted}
				/>
			) : (
				post.link && (
					<div
						className="flex items-center justify-center text-[12px]"
						style={{
							aspectRatio: "1.91 / 1",
							background: c.box,
							color: c.muted,
						}}
					>
						The page has no og:image, so Facebook shows no picture
					</div>
				)
			)}

			{post.link && (
				<div className="px-3 py-2.5" style={{ background: c.box }}>
					<p
						className="truncate text-[13px] uppercase"
						style={{ color: c.muted }}
					>
						{post.link.domain}
					</p>
					<p
						className="mt-0.5 line-clamp-2 text-[17px] font-semibold leading-snug"
						style={{ color: c.text }}
					>
						{post.link.title ?? post.link.url}
					</p>
					{post.link.description && (
						<p
							className="mt-0.5 line-clamp-1 text-[15px]"
							style={{ color: c.muted }}
						>
							{post.link.description}
						</p>
					)}
				</div>
			)}

			<div
				className="mx-4 flex items-center gap-8 border-t py-2.5"
				style={{ borderColor: c.line, color: c.muted }}
			>
				<ThumbsUp className="h-5 w-5" />
				<MessageCircle className="h-5 w-5" />
				<Forward className="h-5 w-5" />
			</div>

			{post.comment && (
				<div
					className="flex items-start gap-2 border-t px-4 py-3"
					style={{ borderColor: c.line }}
				>
					<Avatar
						name={post.account.name}
						picture={post.account.picture}
						size={32}
					/>
					<div className="min-w-0 flex-1">
						<div
							className="inline-block max-w-full rounded-2xl px-3 py-2 text-[15px] leading-snug"
							style={{ background: c.box }}
						>
							<p
								className="text-[13px] font-semibold"
								style={{ color: c.text }}
							>
								{post.account.name}
							</p>
							<p className="break-words" style={{ color: c.text }}>
								{post.comment.text}{" "}
								<span style={{ color: c.link }}>{post.comment.link.url}</span>
							</p>
						</div>
						<div
							className="mt-1.5 flex overflow-hidden rounded-lg border"
							style={{ borderColor: c.line, background: c.box }}
						>
							{post.comment.link.image && (
								<img
									src={post.comment.link.image}
									alt=""
									className="h-[72px] w-[137px] shrink-0 object-cover"
								/>
							)}
							<div className="min-w-0 px-3 py-2">
								<p
									className="truncate text-[12px] uppercase"
									style={{ color: c.muted }}
								>
									{post.comment.link.domain}
								</p>
								<p
									className="line-clamp-2 text-[14px] font-semibold leading-snug"
									style={{ color: c.text }}
								>
									{post.comment.link.title ?? post.comment.link.url}
								</p>
							</div>
						</div>
						<p
							className="mt-1 px-3 text-[12px] font-semibold"
							style={{ color: c.muted }}
						>
							Første kommentar · sættes på automatisk
						</p>
					</div>
				</div>
			)}
		</div>
	);
};

const InstagramPost = ({ post, mode }: { post: SocialPreview; mode: Mode }) => {
	const c = INSTAGRAM[mode];
	return (
		<div
			className="w-full max-w-[470px] overflow-hidden rounded-lg border text-[14px]"
			style={{
				background: c.card,
				borderColor: c.line,
				fontFamily:
					'-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
			}}
		>
			<div className="flex items-center gap-2.5 px-3 py-2.5">
				<Avatar
					name={post.account.name}
					picture={post.account.picture}
					size={32}
				/>
				<p className="flex-1 font-semibold" style={{ color: c.text }}>
					{post.account.name}
				</p>
				<MoreHorizontal className="h-5 w-5" style={{ color: c.text }} />
			</div>
			{post.image ? (
				<PostImage
					src={post.image}
					aspectRatio="4 / 5"
					background={c.line}
					color={c.muted}
				/>
			) : (
				<div
					className="flex items-center justify-center text-[12px]"
					style={{ aspectRatio: "4 / 5", background: c.line, color: c.muted }}
				>
					No image yet: Instagram will refuse the post without one
				</div>
			)}
			<div
				className="flex items-center gap-4 px-3 pt-3"
				style={{ color: c.text }}
			>
				<Heart className="h-6 w-6" />
				<MessageCircle className="h-6 w-6" />
				<Send className="h-6 w-6" />
				<Bookmark className="ml-auto h-6 w-6" />
			</div>
			<div className="px-3 pt-2 pb-3 leading-[18px]">
				<Clamped
					text={post.message}
					lines={2}
					more="mere"
					color={{ text: c.text, muted: c.muted, link: c.link }}
					prefix={
						<span className="mr-1 font-semibold">{post.account.name}</span>
					}
				/>
			</div>
		</div>
	);
};

export const SocialPostPreviews = ({ posts }: { posts: SocialPreview[] }) => {
	const [mode, setMode] = useState<Mode>(storedMode);
	const choose = (next: Mode) => {
		setMode(next);
		try {
			localStorage.setItem(STORAGE_KEY, next);
		} catch {
			return;
		}
	};

	return (
		<div className="mt-2 space-y-2">
			<div className="flex items-center gap-2 text-[11px] text-slate-500">
				<span>Preview of what will be posted</span>
				<div className="ml-auto inline-flex overflow-hidden rounded-md border border-slate-200">
					{(["dark", "light"] as const).map((option) => (
						<button
							key={option}
							type="button"
							onClick={() => choose(option)}
							className={`px-2 py-0.5 ${mode === option ? "bg-slate-800 text-white" : "bg-white text-slate-600"}`}
						>
							{option === "dark" ? "Dark" : "Light"}
						</button>
					))}
				</div>
			</div>
			<div className="flex flex-wrap items-start gap-4">
				{posts.map((post) =>
					post.platform === "instagram" ? (
						<InstagramPost
							key={`${post.nodeId}-${post.platform}`}
							post={post}
							mode={mode}
						/>
					) : (
						<FacebookPost
							key={`${post.nodeId}-${post.platform}`}
							post={post}
							mode={mode}
						/>
					),
				)}
			</div>
		</div>
	);
};
